import { Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Layout } from "@/components/Layout";
import { FaqSection } from "@/components/FaqSection";
import {
  PAKISTAN_CITIES,
  computeCityPrices,
  currencyFor,
  formatMoney,
  karatLabel,
  karatPurity,
  KARATS,
  GRAMS_PER_TOLA,
  type Karat,
} from "@/lib/gold-data";
import { liveTimestamp } from "@/lib/seo";
import { useGoldRates } from "@/lib/nonblocking-rates";
import { RelatedSearches } from "@/components/RelatedSearches";
import { KARAT_KW, WEIGHT_KW, karatClusterFaqs, weightClusterFaqs } from "@/lib/keyword-clusters";

const HistoricalAnalyticsDashboard = lazy(() =>
  import("@/components/HistoricalAnalyticsDashboard").then((m) => ({
    default: m.HistoricalAnalyticsDashboard,
  })),
);

/** Grams for each supported unit slug. */
export const PK_UNIT_GRAMS = {
  "per-tola": GRAMS_PER_TOLA,
  "per-gram": 1,
  "per-10-gram": 10,
  "per-ratti": 0.121,
} as const;

export type PkUnitSlug = keyof typeof PK_UNIT_GRAMS;

export const PK_UNIT_LABEL: Record<PkUnitSlug, string> = {
  "per-tola": "Per Tola (11.6638 g)",
  "per-gram": "Per Gram",
  "per-10-gram": "Per 10 Grams",
  "per-ratti": "Per Ratti (0.121 g)",
};

export const PK_UNIT_SHORT: Record<PkUnitSlug, string> = {
  "per-tola": "tola",
  "per-gram": "gram",
  "per-10-gram": "10 gram",
  "per-ratti": "ratti",
};

function todayLabel() {
  return new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

type Mode =
  | { kind: "karat"; karat: Karat }
  | { kind: "unit"; unit: PkUnitSlug };

export function PakistanMatrixPage(props: {
  mode: Mode;
  h1: string;
  intro: string;
}) {
  const { data } = useGoldRates();
  const currency = currencyFor("pakistan");
  const updated = liveTimestamp(data.fetchedAt, "PKT");
  const date = todayLabel();

  const rows = PAKISTAN_CITIES.map((c) => {
    const p = computeCityPrices({
      spotUsdPerOz: data.spotUsdPerOz,
      fxRate: data.pkrPerUsd,
      country: "pakistan",
      cityPremium: c.premium,
    });
    return { city: c, prices: p };
  });

  const isKarat = props.mode.kind === "karat";
  const karat: Karat = props.mode.kind === "karat" ? props.mode.karat : "24k";
  const unit: PkUnitSlug = props.mode.kind === "unit" ? props.mode.unit : "per-tola";
  const grams = PK_UNIT_GRAMS[unit];
  const purity = karatPurity(karat);

  const headline = rows[0];
  const headlinePerGram24 = headline.prices.perGram24;
  const headlinePrice = headlinePerGram24 * (isKarat ? purity : 1) * (isKarat ? GRAMS_PER_TOLA : grams);

  // Karat mode: rows show tola / 10g / gram at that karat
  // Unit mode: rows show 24K/22K/21K at that unit
  const columns = isKarat
    ? [
        { label: "Per Tola", grams: GRAMS_PER_TOLA },
        { label: "Per 10 Grams", grams: 10 },
        { label: "Per Gram", grams: 1 },
      ]
    : [
        { label: "24K", karat: "24k" as Karat },
        { label: "22K", karat: "22k" as Karat },
        { label: "21K", karat: "21k" as Karat },
        { label: "18K", karat: "18k" as Karat },
      ];

  const faqs = isKarat
    ? [...buildKaratFaqs(karat), ...karatClusterFaqs(karat === "14k" ? "18k" : (karat as "24k" | "22k" | "21k" | "18k"), "pakistan")]
    : [...buildUnitFaqs(unit), ...(unit === "per-ratti" ? [] : weightClusterFaqs(unit as "per-tola" | "per-gram" | "per-10-gram"))];

  const clusterKw = isKarat
    ? (karat === "14k" ? KARAT_KW["18k"] : KARAT_KW[karat as "24k" | "22k" | "21k" | "18k"])
    : (unit === "per-ratti" ? [] : WEIGHT_KW[unit as "per-tola" | "per-gram" | "per-10-gram"]);
  const relatedLinks = clusterKw.map((label) => ({
    label,
    href: isKarat ? `/pakistan/${karat}` : `/pakistan/${unit}`,
  }));

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: isKarat
      ? `${karatLabel(karat)} Gold in Pakistan (per tola)`
      : `Gold in Pakistan ${PK_UNIT_LABEL[unit]}`,
    description: props.intro,
    brand: { "@type": "Brand", name: "DrHint" },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "PKR",
      lowPrice: Math.round(rows[rows.length - 1].prices.perGram24 * purity * grams).toString(),
      highPrice: Math.round(headlinePrice).toString(),
      offerCount: rows.length,
      priceValidUntil: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    },
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://drhint.com/" },
      { "@type": "ListItem", position: 2, name: "Pakistan", item: "https://drhint.com/pakistan" },
      {
        "@type": "ListItem",
        position: 3,
        name: isKarat ? karatLabel(karat) : PK_UNIT_LABEL[unit],
        item: `https://drhint.com/pakistan/${isKarat ? karat : unit}`,
      },
    ],
  };

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 py-8">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link> ›{" "}
          <Link to="/pakistan" className="hover:underline">Pakistan</Link> ›{" "}
          {isKarat ? karatLabel(karat) : PK_UNIT_LABEL[unit]}
        </nav>

        <h1 className="text-3xl md:text-4xl font-bold">{props.h1}</h1>
        <p className="mt-2 text-muted-foreground">{props.intro}</p>

        <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-green-500/40 bg-green-500/10 px-3 py-1 text-xs font-semibold text-green-700 dark:text-green-400" data-nosnippet>
          <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
          🟢 Live · Updated <span data-nosnippet><time dateTime={new Date(data.fetchedAt).toISOString()}>{updated}</time></span>
        </div>

        {/* 1. CHART on top — specific to this karat / unit */}
        <div className="mt-6">
          <Suspense fallback={<div className="min-h-[280px] rounded-xl border border-border bg-card animate-pulse" />}>
            <HistoricalAnalyticsDashboard
              city="Pakistan"
              cluster="gold"
              currentPrice={headlinePrice}
              currency={currency}
              unitLabel={isKarat ? `${karatLabel(karat)} · Per Tola` : `24K · ${PK_UNIT_LABEL[unit]}`}
            />
          </Suspense>
        </div>

        {/* 2. Big gold-colored headline price — matches /pakistan hub */}
        <div className="mt-6 relative overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-br from-card via-card to-gold/5 p-6 md:p-8 shadow-lg">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
            <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            Live · {isKarat ? karatLabel(karat) : "24K"} Gold · Pakistan
          </div>
          <div className="mt-2 text-sm text-muted-foreground">
            Today {isKarat ? `${karatLabel(karat)} Gold` : "24K Gold"} — {isKarat ? "Per Tola" : PK_UNIT_LABEL[unit]}
          </div>
          <div className="mt-2 text-4xl md:text-6xl font-extrabold tracking-tight text-gold leading-none">
            {formatMoney(headlinePrice, currency)}
          </div>
          <div className="mt-3 text-xs font-medium text-primary" data-nosnippet>
            Updated: <span data-nosnippet><time dateTime={new Date(data.fetchedAt).toISOString()}>{updated}</time></span>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4">
          <p className="text-sm">
            <strong>Aaj <span data-nosnippet>{date}</span></strong> ko Pakistan mein{" "}
            {isKarat
              ? `${karatLabel(karat)} sone ka rate Karachi mein ${formatMoney(headlinePrice, currency)} per tola`
              : `24K sone ka rate Karachi mein ${formatMoney(headlinePrice, currency)} ${PK_UNIT_SHORT[unit]}`}{" "}
            hai. Ye rate international spot + USD/PKR se every 10 minutes recalc hota hai.
          </p>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 text-xs">
          <span className="font-semibold text-muted-foreground">Karats:</span>
          {KARATS.filter((k) => k !== "14k").map((k) => (
            <Link
              key={k}
              to={`/pakistan/${k}`}
              className={`rounded-full border px-3 py-1 ${isKarat && k === karat ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
            >
              {karatLabel(k)}
            </Link>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <span className="font-semibold text-muted-foreground">Units:</span>
          {(Object.keys(PK_UNIT_GRAMS) as PkUnitSlug[]).map((u) => (
            <Link
              key={u}
              to={`/pakistan/${u}`}
              className={`rounded-full border px-3 py-1 ${!isKarat && u === unit ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
            >
              {PK_UNIT_LABEL[u]}
            </Link>
          ))}
        </div>

        <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <caption className="sr-only" data-nosnippet>
              {isKarat
                ? `Live ${karatLabel(karat)} gold rate in every major Pakistani city, ${date}`
                : `Live gold rate ${PK_UNIT_LABEL[unit]} across every karat in every major Pakistani city, ${date}`}
            </caption>
            <thead className="bg-muted/60 text-left">
              <tr>
                <th scope="col" className="px-3 py-3 font-semibold">City</th>
                {columns.map((col) => (
                  <th key={col.label} scope="col" className="px-3 py-3 font-semibold text-right">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ city, prices }) => (
                <tr key={city.slug} className="border-t border-border">
                  <th scope="row" className="px-3 py-3 text-left font-medium">
                    <Link
                      to="/$country/$city"
                      params={{ country: "pakistan", city: city.slug }}
                      className="hover:text-primary hover:underline"
                    >
                      {city.name}
                    </Link>
                  </th>
                  {columns.map((col) => {
                    const value = isKarat
                      ? prices.perGram24 * purity * ("grams" in col ? col.grams : 1)
                      : prices.perGram24 * karatPurity(("karat" in col ? col.karat : "24k") as Karat) * grams;
                    return (
                      <td key={col.label} className="px-3 py-3 text-right font-semibold tabular-nums">
                        {formatMoney(value, currency)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <article className="prose prose-sm max-w-none mt-8 text-foreground/90">
          <h2 className="text-xl font-bold">
            {isKarat
              ? <>About {karatLabel(karat)} gold rate in Pakistan <span data-nosnippet>({date})</span></>
              : <>About gold rate {PK_UNIT_LABEL[unit]} in Pakistan <span data-nosnippet>({date})</span></>}
          </h2>
          <p>
            {isKarat
              ? `${karatLabel(karat)} gold is ${Math.round(purity * 1000) / 10}% pure. In Pakistan the ${karatLabel(karat)} rate is standardised across the ${PAKISTAN_CITIES.length} major sarafa bazaars (Karachi, Lahore, Islamabad, Rawalpindi, Faisalabad, Multan, Peshawar, Quetta and more) with a small city-level premium. We recalculate from the international XAU/USD spot every 10 minutes and add today's live USD/PKR conversion, so the rate above is what jewellers in each city quote before making charges and applicable tax.`
              : `The ${PK_UNIT_LABEL[unit]} gold rate in Pakistan is derived from the international spot price (USD/oz) converted to PKR at today's live exchange rate. ${PK_UNIT_LABEL[unit]} equals ${grams} gram(s), so the price scales linearly from the per-gram rate. The table above shows every karat across all ${PAKISTAN_CITIES.length} major cities, refreshed every 10 minutes.`}
          </p>
          <p>
            <strong>Roman Urdu:</strong>{" "}
            {isKarat
              ? `Aaj Pakistan mein ${karatLabel(karat)} sona ka bhao har city mein thoda alag hai — Karachi sarafa bazaar sabse bara reference market hai. Lahore, Islamabad aur Rawalpindi ka rate almost same hota hai. Chhote cities mein 0.1%–0.2% premium hota hai transport aur dealer margin ki wajah se.`
              : `Pakistan mein sona ka rate ${PK_UNIT_SHORT[unit]} mein aaj ${formatMoney(headlinePrice, currency)} chal raha hai Karachi mein. Baaki cities mein hlkka farak aata hai. Har 10 minute mein update hota hai international spot aur dollar rate ke hisaab se.`}
          </p>
        </article>

        <FaqSection
          faqs={faqs}
          title={
            isKarat
              ? `${karatLabel(karat)} gold rate in Pakistan — FAQs`
              : `Gold rate ${PK_UNIT_LABEL[unit]} in Pakistan — FAQs`
          }
        />

        <RelatedSearches
          title={isKarat ? `People also search — ${karatLabel(karat)} gold in Pakistan` : `People also search — gold ${PK_UNIT_LABEL[unit]} in Pakistan`}
          links={relatedLinks}
          className="px-0"
        />

        <div className="mt-10 rounded-xl border border-border bg-muted/40 p-4 text-sm">
          <h3 className="font-semibold">Related pages</h3>
          <ul className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <li>
              <Link to="/pakistan" className="text-primary hover:underline">
                Full Pakistan gold rate hub
              </Link>
            </li>
            {(isKarat
              ? (Object.keys(PK_UNIT_GRAMS) as PkUnitSlug[]).map((u) => ({
                  to: `/pakistan/${u}`,
                  label: `${karatLabel(karat)} gold ${PK_UNIT_LABEL[u]} in Pakistan`,
                }))
              : (KARATS.filter((k) => k !== "14k") as Karat[]).map((k) => ({
                  to: `/pakistan/${k}`,
                  label: `${karatLabel(k)} gold ${PK_UNIT_LABEL[unit]} in Pakistan`,
                }))
            ).map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-primary hover:underline">
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/$country/$city"
                params={{ country: "pakistan", city: "karachi" }}
                className="text-primary hover:underline"
              >
                Karachi live gold rate
              </Link>
            </li>
            <li>
              <Link
                to="/$country/$city"
                params={{ country: "pakistan", city: "lahore" }}
                className="text-primary hover:underline"
              >
                Lahore live gold rate
              </Link>
            </li>
          </ul>
        </div>
      </section>
    </Layout>
  );
}

function buildKaratFaqs(karat: Karat) {
  const k = karatLabel(karat);
  return [
    {
      q: `Aaj Pakistan mein ${k} sone ka rate kya hai?`,
      a: `${k} gold ka rate Pakistan mein har 10 minute mein update hota hai international XAU/USD spot price aur live USD/PKR exchange rate se. Karachi sarafa bazaar reference hai; baaki cities mein 0.1%–0.2% ka farak hota hai.`,
    },
    {
      q: `${k} gold aur 24K gold mein kya difference hai?`,
      a: `${k} gold ${Math.round(karatPurity(karat) * 1000) / 10}% pure hota hai, jabki 24K 99.9% pure hai. ${karat === "22k" || karat === "21k" ? "Jewellery ke liye " + k + " zyada durable hoti hai kyunki alloy metals mila hote hain." : "24K bullion aur investment coins ke liye best hai; jewellery mostly 22K/21K hoti hai."}`,
    },
    {
      q: `Kya Karachi aur Lahore mein ${k} rate same hota hai?`,
      a: `Reference rate national hota hai lekin har city ka premium thoda alag hai. Karachi (0%), Lahore (+0.08%), Islamabad (+0.12%), Peshawar (+0.15%) — transport, dealer margin, aur local demand ka farak hota hai.`,
    },
    {
      q: `${k} gold khareedne ka best time kya hai Pakistan mein?`,
      a: `Rate hourly change hota hai, lekin historical trends dekhen to mid-week (Tuesday–Wednesday) morning slots mein rate thoda settle hota hai. Bara khareed ke liye price alert lagana aur 3–5 din ki average dekh kar decide karna sahi hai.`,
    },
    {
      q: `${k} gold par kitna tax lagta hai Pakistan mein?`,
      a: `Retail bullion par 17% GST applicable hoti hai (jewellery form mein). Bara amount ke liye SRO exemptions available hain. Making charges alag se 8%–20% add hoti hain depending on jewellery design.`,
    },
  ];
}

function buildUnitFaqs(unit: PkUnitSlug) {
  const u = PK_UNIT_LABEL[unit];
  const grams = PK_UNIT_GRAMS[unit];
  return [
    {
      q: `Aaj Pakistan mein sone ka rate ${u} kya hai?`,
      a: `${u} 24K gold ka rate Karachi mein reference hota hai. Ye value = per-gram rate × ${grams}. Har 10 minute mein international spot + USD/PKR exchange rate se update hota hai.`,
    },
    {
      q: `1 tola aur 10 gram mein kya farak hai?`,
      a: `1 tola = 11.6638 gram. Pakistan mein tola sabse common unit hai; India mein 10 gram standard hai. 10 gram gold Pakistan mein bhi widely quote hota hai bullion trading ke liye.`,
    },
    {
      q: `Ratti kya hoti hai?`,
      a: `1 ratti = 0.121 gram (approx). Chhoti jewellery items — nose pins, stone settings — ratti mein weigh hoti hain. Bara khareed ke liye tola ya gram use hota hai.`,
    },
    {
      q: `Kaunsi city mein sone ka rate ${u} sabse kam hota hai?`,
      a: `Karachi generally reference rate hoti hai (0% premium). Choti cities jaisa Bahawalpur, Sukkur, Larkana mein 0.1%–0.16% zyada rate hoti hai due to transport aur limited dealer network. Islamabad/Lahore beech mein rehte hain.`,
    },
    {
      q: `${u} gold rate roz kyun change hoti hai?`,
      a: `Har minute international spot (XAU/USD) change hota hai — Fed policy, dollar strength, inflation, geopolitics. Iske upar USD/PKR rate bhi shift hota hai. Dono milke Pakistan retail rate ${u} bhi hourly move karta hai.`,
    },
  ];
}